/* ScanVisitCaseView — iter97.88 (Phase A4 prep)
 *
 * Trim, focused view rendered on the doctor's CaseDetail page whenever a
 * case is in `scan_visit_scheduled` or `scan_visit_completed`. At those
 * stages there's almost no clinical data captured yet, so a full case
 * page is just noise — instead we show only what's real:
 *
 *   1. Status hero — clear messaging + Reschedule / Cancel actions
 *   2. Patient + visit tiles
 *   3. Booking commitment (fee snapshot + payment choice)
 *   4. Activity timeline (booking + tech-assigned + completed events)
 *
 * When the case transitions to `scan_visit_completed`, the hero flips
 * to a primary "Continue Case Wizard" CTA + a scans/photos preview.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import TimeSlotSelect from "@/components/common/TimeSlotSelect";
import {
    Calendar, ClockClockwise, MapPin, Phone, User, Wrench, ArrowRight,
    CheckCircle, X as CloseIcon, PencilSimple, Trash, FileArrowDown,
    Cube, Image as ImageIcon, Receipt,
} from "@phosphor-icons/react";

const TONE = {
    scheduled: {
        wrapper: "border-amber-200 bg-amber-50",
        chip:    "bg-amber-100 text-amber-900",
        icon:    "text-amber-700",
    },
    completed: {
        wrapper: "border-primary/30 bg-primary/5",
        chip:    "bg-primary/15 text-primary",
        icon:    "text-primary",
    },
};

function relativeDateLabel(dateStr) {
    if (!dateStr) return "";
    try {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const d = new Date(dateStr + "T00:00:00");
        const diff = Math.round((d - today) / (24 * 60 * 60 * 1000));
        if (diff === 0) return "today";
        if (diff === 1) return "tomorrow";
        if (diff === -1) return "yesterday";
        if (diff > 1) return `in ${diff} days`;
        return `${Math.abs(diff)} days ago`;
    } catch { return ""; }
}

function fmtDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso; }
}
function fmtDateTime(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
}

export default function ScanVisitCaseView({ caseRecord, patient, onChanged }) {
    const navigate = useNavigate();
    const c = caseRecord;
    const isCompleted = c.status === "scan_visit_completed";
    const tone = isCompleted ? TONE.completed : TONE.scheduled;

    const [visit, setVisit] = useState(null);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);

    const [rescheduleOpen, setRescheduleOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);

    // Pull the linked scan visit + uploaded scan/photo files in one
    // round-trip. The case has `scan_visit_id`; the doctor-side
    // /doctor/scan-visits/{id} endpoint returns the visit detail and
    // /doctor/scan-visits/{id}/files returns every scan + photo file the
    // technician uploaded during the visit.
    useEffect(() => {
        if (!c?.id) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [visitResp, filesResp] = await Promise.all([
                    c.scan_visit_id
                        ? api.get(`/doctor/scan-visits/${c.scan_visit_id}`).catch(() => ({ data: null }))
                        : Promise.resolve({ data: null }),
                    c.scan_visit_id
                        ? api.get(`/doctor/scan-visits/${c.scan_visit_id}/files`).catch(() => ({ data: [] }))
                        : Promise.resolve({ data: [] }),
                ]);
                if (cancelled) return;
                setVisit(visitResp.data);
                setFiles(filesResp.data || []);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [c?.id, c?.scan_visit_id]);

    // iter98.68 — Anonymize technician identity in doctor-facing views.
    // The real `assigned_technician_name` is preserved on the visit
    // document for admin + audit surfaces but never surfaced to the
    // doctor. `techAssigned` controls the "assigned vs pending"
    // branch; `techName` is the constant label rendered in either
    // case once a technician has been assigned.
    const techAssigned = !!(visit?.assigned_technician_id || visit?.assigned_technician_name || visit?.technician_name);
    const techName = "Center of Excellence Team";
    const techPhone = visit?.assigned_technician_phone || "";

    return (
        <div className="space-y-4" data-testid="scan-visit-case-view">
            {/* ─── HERO ─────────────────────────────────────────────────── */}
            <div className={`rounded-2xl border p-5 ${tone.wrapper}`} data-testid="scan-visit-hero">
                {isCompleted ? (
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <span className={`text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full ${tone.chip}`}>
                                    Scans uploaded
                                </span>
                            </div>
                            <h2 className="font-display text-xl font-bold">
                                ✅ Scans &amp; photos uploaded
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                The <b>{techName}</b> captured{" "}
                                {files.length} file{files.length === 1 ? "" : "s"} on {fmtDate(visit?.completed_at)}.
                                Continue your case wizard to add the treatment plan.
                            </p>
                        </div>
                        <Button
                            size="lg"
                            onClick={() => navigate(`/doctor/cases/new?draft=${c.id}`)}
                            data-testid="scan-visit-continue-wizard-btn"
                        >
                            Continue Case Wizard
                            <ArrowRight size={14} weight="bold" className="ml-1.5" />
                        </Button>
                    </div>
                ) : (
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <span className={`text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full ${tone.chip}`}>
                                    Scan visit scheduled
                                </span>
                            </div>
                            <h2 className="font-display text-xl font-bold">
                                Visit booked for {fmtDate(visit?.date) || "—"}
                                {visit?.time && <span className="text-muted-foreground"> · {visit.time}</span>}
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                {techAssigned ? (
                                    <>
                                        The <b>{techName}</b> will visit your clinic {visit?.date ? relativeDateLabel(visit.date) : ""}.
                                        We&apos;ll notify you the moment scans &amp; photos are uploaded.
                                    </>
                                ) : (
                                    <>
                                        Visit booked. The <b>{techName}</b> will confirm a technician shortly — you&apos;ll see the slot lock here once it&apos;s finalised.
                                    </>
                                )}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setRescheduleOpen(true)}
                                data-testid="scan-visit-reschedule-btn"
                            >
                                <PencilSimple size={13} className="mr-1.5" /> Reschedule
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                className="text-rose-700 border-rose-200 hover:bg-rose-50"
                                onClick={() => setCancelOpen(true)}
                                data-testid="scan-visit-cancel-btn"
                            >
                                <Trash size={13} className="mr-1.5" /> Cancel case
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* ─── PATIENT + VISIT TILES ────────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <InfoTile
                    title="Patient"
                    testid="scan-visit-patient-tile"
                    rows={[
                        { icon: User,  label: patient?.name || c.patient_name || "—" },
                        { icon: Phone, label: patient?.phone || c.patient_phone || "—" },
                        ...(patient?.gender ? [{ icon: User, label: patient.gender }] : []),
                        ...(patient?.dob ? [{ icon: Calendar, label: `DOB ${patient.dob}` }] : []),
                    ]}
                    footer={
                        patient?.id ? (
                            <Link to={`/doctor/patients/${patient.id}`} className="text-xs text-primary font-semibold hover:underline">
                                Open patient profile →
                            </Link>
                        ) : null
                    }
                />
                <InfoTile
                    title="Visit details"
                    testid="scan-visit-visit-tile"
                    rows={[
                        { icon: Calendar,        label: loading ? "Loading…" : (visit ? `${fmtDate(visit.date)}${visit.time ? ` · ${visit.time}` : ""}` : "Visit not found"), muted: !visit && !loading },
                        ...(visit?.date ? [{ icon: ClockClockwise, label: relativeDateLabel(visit.date) }] : []),
                        ...(techAssigned
                            ? [{ icon: Wrench, label: techName, sub: techPhone }]
                            : [{ icon: Wrench, label: "Awaiting technician assignment", muted: true }]
                        ),
                        ...(visit?.notes ? [{ icon: PencilSimple, label: visit.notes, muted: true }] : []),
                    ]}
                />
            </div>

            {/* ─── BOOKING COMMITMENT ────────────────────────────────── */}
            {c.io_scanning_fee_at_booking != null && (
                <div className="bg-card border border-border rounded-2xl p-4" data-testid="scan-visit-booking-tile">
                    <div className="flex items-center gap-2 mb-2">
                        <Receipt size={14} className="text-muted-foreground" />
                        <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Booking commitment</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div>
                            <div className="text-[11px] text-muted-foreground">Fee at booking</div>
                            <div className="font-display text-xl font-bold">₹{Math.round(c.io_scanning_fee_at_booking).toLocaleString("en-IN")}</div>
                            <div className="text-[11px] text-muted-foreground">Locked in — won&apos;t change even if Invisique™ updates pricing.</div>
                        </div>
                        <div>
                            <div className="text-[11px] text-muted-foreground">Payment</div>
                            <div className="font-medium">Add to final invoice</div>
                            <div className="text-[11px] text-muted-foreground">
                                Will appear as a line item on the case&apos;s final invoice.
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── SCANS + PHOTOS PREVIEW (only when completed) ─────────── */}
            {isCompleted && files.length > 0 && (
                <div className="bg-card border border-border rounded-2xl p-4" data-testid="scan-visit-files-preview">
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Captured records</h3>
                        <span className="text-xs text-muted-foreground">{files.length} item{files.length === 1 ? "" : "s"}</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2">
                        {files.map((f) => (
                            <FilePreview key={f.file_id} f={f} />
                        ))}
                    </div>
                </div>
            )}

            {/* ─── ACTIVITY TIMELINE ─────────────────────────────────── */}
            <div className="bg-card border border-border rounded-2xl p-4" data-testid="scan-visit-timeline">
                <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground mb-3">Activity</h3>
                <ol className="relative border-l border-border ml-1.5 space-y-3 pl-4">
                    {visit?.completed_at && (
                        <TimelineItem
                            dotTone="primary"
                            title="Scans &amp; photos uploaded"
                            sub={`${techName} · ${fmtDateTime(visit.completed_at)}`}
                        />
                    )}
                    {visit?.assigned_at && (
                        <TimelineItem
                            dotTone="amber"
                            title={`Technician assigned${techAssigned ? ` — ${techName}` : ""}`}
                            sub={fmtDateTime(visit.assigned_at)}
                        />
                    )}
                    <TimelineItem
                        dotTone="neutral"
                        title="Scan visit booked"
                        sub={fmtDateTime(c.created_at)}
                    />
                </ol>
            </div>

            {/* Reschedule modal */}
            <RescheduleDialog
                open={rescheduleOpen}
                onOpenChange={setRescheduleOpen}
                visit={visit}
                onRescheduled={() => { setRescheduleOpen(false); onChanged?.(); }}
            />

            {/* Cancel confirmation */}
            <CancelDialog
                open={cancelOpen}
                onOpenChange={setCancelOpen}
                caseId={c.id}
                techName={techName}
                onCancelled={() => { setCancelOpen(false); onChanged?.(); navigate("/doctor/cases"); }}
            />
        </div>
    );
}

function InfoTile({ title, rows, footer, testid }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-4" data-testid={testid}>
            <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground mb-3">{title}</h3>
            <ul className="space-y-2">
                {rows.map((r, i) => {
                    const Icon = r.icon;
                    return (
                        <li key={i} className="flex items-start gap-2 text-sm">
                            <Icon size={14} className={`mt-0.5 shrink-0 ${r.muted ? "text-muted-foreground" : "text-muted-foreground"}`} />
                            <div className="min-w-0">
                                <div className={r.muted ? "text-muted-foreground italic" : "font-medium"}>{r.label}</div>
                                {r.sub && <div className="text-[11px] text-muted-foreground">{r.sub}</div>}
                            </div>
                        </li>
                    );
                })}
            </ul>
            {footer && <div className="mt-3 pt-3 border-t border-border">{footer}</div>}
        </div>
    );
}

function TimelineItem({ dotTone, title, sub }) {
    const dotClass = {
        primary: "bg-primary",
        amber:   "bg-amber-500",
        neutral: "bg-foreground/40",
    }[dotTone] || "bg-foreground/40";
    return (
        <li>
            <span className={`absolute -left-[7px] mt-1 w-3 h-3 rounded-full ${dotClass} ring-4 ring-card`} />
            <div className="text-sm font-medium" dangerouslySetInnerHTML={{ __html: title }} />
            {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
        </li>
    );
}

function FilePreview({ f }) {
    const isPhoto = (f.kind || "").startsWith("photo_") || (f.mime || "").startsWith("image/");
    const Icon = isPhoto ? ImageIcon : Cube;
    const token = typeof window !== "undefined" ? localStorage.getItem("ca_token") || "" : "";
    const url = `${API}/files/${f.file_id}?token=${encodeURIComponent(token)}`;
    return (
        <div className="border border-border rounded-xl p-2 bg-secondary/30">
            <div className="aspect-square rounded-lg bg-secondary flex items-center justify-center mb-1 overflow-hidden">
                {isPhoto ? (
                    <img src={url} alt={f.file_name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                    <Icon size={28} className="text-muted-foreground" />
                )}
            </div>
            <div className="text-[10px] font-semibold text-foreground/80 truncate">{f.kind.replace(/_/g, " ")}</div>
            <div className="text-[10px] text-muted-foreground truncate">{f.file_name}</div>
        </div>
    );
}

function RescheduleDialog({ open, onOpenChange, visit, onRescheduled }) {
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && visit) {
            setDate(visit.date || "");
            setTime(visit.time || "");
        }
    }, [open, visit]);

    const submit = async () => {
        if (!date || !time) { toast.error("Pick a date and time"); return; }
        if (!visit?.id) { toast.error("Visit not loaded"); return; }
        setSaving(true);
        try {
            await api.patch(`/doctor/scan-visits/${visit.id}`, { date, time });
            toast.success("Visit rescheduled");
            onRescheduled?.();
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" && detail ? detail : "Could not reschedule — please try again");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onOpenChange?.(false)}>
            <DialogContent className="max-w-md" data-testid="scan-visit-reschedule-dialog">
                <DialogHeader>
                    <DialogTitle>Reschedule scan visit</DialogTitle>
                    <DialogDescription>
                        Pick a new date and time. Invisique™ ops + the assigned technician will see the update.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3 mt-2">
                    <div>
                        <Label className="text-xs font-semibold">Date</Label>
                        <Input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="h-10 mt-1"
                            data-testid="scan-visit-reschedule-date"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Time</Label>
                        <div className="mt-1">
                            <TimeSlotSelect
                                value={time}
                                onChange={setTime}
                                date={date}
                                testId="scan-visit-reschedule-time"
                            />
                        </div>
                    </div>
                </div>
                <DialogFooter className="mt-3">
                    <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} data-testid="scan-visit-reschedule-submit">
                        {saving ? "Saving…" : "Confirm reschedule"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CancelDialog({ open, onOpenChange, caseId, techName, onCancelled }) {
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (!open) setReason(""); }, [open]);

    const submit = async () => {
        if ((reason || "").trim().length < 3) { toast.error("Tell us why so our team knows"); return; }
        setSaving(true);
        try {
            await api.put(`/cases/${caseId}/cancel`, { reason: reason.trim() });
            toast.success("Case cancelled — the scan visit was also cancelled");
            onCancelled?.();
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" && detail ? detail : "Could not cancel — please try again");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onOpenChange?.(false)}>
            <DialogContent className="max-w-md" data-testid="scan-visit-cancel-dialog">
                <DialogHeader>
                    <DialogTitle>Cancel this case?</DialogTitle>
                    <DialogDescription>
                        This cancels both the case and the booked scan visit
                        {techName ? <> — the <b>{techName}</b> will be notified.</> : "."}
                        {" "}This action is final; you&apos;ll need to start a fresh case if the patient comes back.
                    </DialogDescription>
                </DialogHeader>
                <div className="mt-2">
                    <Label className="text-xs font-semibold">Reason</Label>
                    <Textarea
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Patient backed out of treatment, switching to in-house scans, etc."
                        className="mt-1"
                        data-testid="scan-visit-cancel-reason"
                    />
                </div>
                <DialogFooter className="mt-3">
                    <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving}>Keep case</Button>
                    <Button onClick={submit} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="scan-visit-cancel-confirm">
                        {saving ? "Cancelling…" : "Cancel case"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
